import { authFetch, customEncode } from "../DecryptData";

/**
 * =========================================================
 * CONFIGURATION
 * =========================================================
 */
/**
 * Base URL resolution, in priority order:
 *   1. window.BASE_URL            — runtime override
 *   2. REACT_APP_BASE_URL         — .env.development / .env.local
 *   3. API_ORIGIN below           — the live backend, absolute
 *
 * The base is now ABSOLUTE in every environment, so localhost:3000 no longer
 * needs a special case: the old relative "apisecure/" fallback resolved to
 * http://localhost:3000/apisecure/... and never left the CRA dev server, which
 * answered `Cannot POST /apisecure/GetUserDetails...` — a 404 that reads exactly
 * like a backend outage.
 */
/**
 * The live backend origin.
 *
 * ⚠️ The API moved off `anuplights.in` (that box is unreachable on every port).
 * It now runs on the anuplights.com machine, published as smartsearch.anuplights.com,
 * which proxies straight to the Node app on 127.0.0.1:8083. Only `/api/` is
 * mounted there — `/apisecure/` returns 404 — so the base ends in `/api/`.
 *
 * This is absolute rather than relative because smartsearch.live's own vhost
 * still proxies /apisecure/ to the dead host and answers 503. Once that vhost is
 * repointed (ProxyPass /apisecure/ -> http://127.0.0.1:8083/api/), the production
 * branch below can go back to the relative "apisecure/" and drop the cross-origin
 * hop. The backend sends Access-Control-Allow-Origin: * , so this works meanwhile.
 */
const API_ORIGIN = "https://smartsearch.anuplights.com/api/";

let BASE_URL =
  (typeof window !== "undefined" && window.BASE_URL) ||
  process.env.REACT_APP_BASE_URL ||
  API_ORIGIN;

export const setBaseUrl = (url) => {
  if (!url) return;
  BASE_URL = url.endsWith("/") ? url : url + "/";
};

/**
 * Current API base (always ends with "/").
 * Use this instead of reading process.env.REACT_APP_BASE_URL directly — that
 * variable is undefined unless an .env file sets it, which silently produced
 * URLs like "undefinedUpdateUsers".
 */
export const getBaseUrl = () => BASE_URL;

/** An apiName that is already a complete URL is used verbatim. */
const ABSOLUTE_URL = /^https?:\/\//i;

/**
 * Base for routes registered only under the backend's open `/api/` prefix
 * (the SIR endpoints, AddUsers, the offline beacon).
 *
 * ⚠️ Two very different deployments, and only one of them has an `/api/` path:
 *
 * 1. RELATIVE base (production, e.g. https://smartsearch.live) — the app is
 *    served by a reverse proxy that exposes exactly ONE backend path:
 *        /apisecure/*  ->  backend /api/*
 *    There is no /api/ on the web origin; it falls through to the SPA and
 *    returns index.html with HTTP 200. So the open base must stay "apisecure/",
 *    and the proxy does the /api/ rewrite for us.
 *
 * 2. ABSOLUTE base (dev, or anything pointing straight at host:8083) — talks to
 *    Express directly, where /api/ and /apisecure/ are both real. Rewrite it.
 */
export const getOpenBaseUrl = () =>
  ABSOLUTE_URL.test(BASE_URL)
    ? BASE_URL.replace(/apisecure\/?$/, "api/")
    : BASE_URL;

/**
 * =========================================================
 * DEBUG FLAG (TURN ON/OFF API DEBUG)
 * =========================================================
 */
export const API_DEBUG =
  typeof window !== "undefined"
    ? window.API_DEBUG === true
    : false;


/**
 * =========================================================
 * INTERNAL HELPERS
 * =========================================================
 */

const wait = (ms) => new Promise((res) => setTimeout(res, ms));

const fetchWithTimeout = async (url, options = {}, timeout = 10000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const res = await authFetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(id);
    return res;
  } catch (err) {
    clearTimeout(id);
    throw err;
  }
};

export async function FetchImageByImageId(imageId) {
  const res = await authFetch(
    `${BASE_URL}GetImageUrlbyImageIdID`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ImageId: Number(imageId), // ✅ SENT DIRECTLY
      }),
    }
  );

  if (!res.ok) {
    throw new Error("Failed to fetch image URL");
  }

  return await res.json();
}




const callServer = async (
  apiName,
  payload,
  {
    method = "POST",
    retries = 2,
    backoff = 500,
    timeout = 10000,
    plain, // optional override
    open = false, // route via the open /api/ base instead of /apisecure/
  } = {}
) => {
  if (!apiName) {
    throw new Error("API name is required");
  }

  // `open` selects the /api/ base. Never concatenate a base into `apiName` at
  // the call site: when the base is RELATIVE ("api/") it does not start with
  // http, so the branch below would prepend BASE_URL again and produce
  // "apisecure/api/Foo" — the /api/api/ 404s.
  const url = ABSOLUTE_URL.test(apiName)
    ? apiName
    : `${open ? getOpenBaseUrl() : BASE_URL}${apiName}`;

  // ✅ AUTO-DETECT plain payload
  const isPlain =
    typeof plain === "boolean"
      ? plain
      : payload &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      !("data" in payload);


  let attempt = 0;

  while (true) {
    try {
      if (API_DEBUG) {
  // console.log(
  //   `[API CALL] ${apiName} → ${isPlain ? "PLAIN" : "ENCRYPTED"}`,
  //   payload
  // );
}

      const res = await fetchWithTimeout(
        url,
        {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body:
            payload !== undefined
              ? isPlain
                ? JSON.stringify(payload)
                : JSON.stringify({
                  data: customEncode(JSON.stringify(payload)),
                })
              : undefined,


        },
        timeout
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`HTTP ${res.status}: ${text}`);
      }

      // A same-origin path that is not proxied to the API falls through to the
      // SPA and returns index.html with HTTP 200. Without this check res.json()
      // fails with a bare "Unexpected token '<'", which says nothing about the
      // real problem (the request never reached the backend).
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("json")) {
        const text = await res.text();
        throw new Error(
          `Expected JSON from ${url} but received "${contentType || "no content-type"}". ` +
          `The request was not routed to the API. First bytes: ${text.slice(0, 80)}`
        );
      }

      return await res.json();
    } catch (err) {
      const isNetwork =
        err.name === "AbortError" ||
        /network|failed|ECONNREFUSED/i.test(err.message);

      attempt++;
      if (attempt > retries) {
        const out = new Error(
          isNetwork
            ? "NetworkError: Unable to reach server"
            : err.message
        );
        out.original = err;
        throw out;
      }

      await wait(backoff * attempt);
    }
  }
};

/**
 * =========================================================
 * ✅ PUBLIC API – USE ONLY THESE 4 METHODS
 * =========================================================
 */

export const InsertToServer = (apiName, payload, options) => {
  return callServer(apiName, payload, options);
};

export const FetchFromServer = (apiName, payload, options) => {
  return callServer(apiName, payload, options);
};

export const UpdateToServer = (apiName, payload, options) => {
  return callServer(apiName, payload, options);
};

export const DeleteFromServer = (apiName, payload, options) => {
  return callServer(apiName, payload, options);
};

/**
 * =========================================================
 * ✅ PLAIN (NON-ENCRYPTED) API CALL
 * =========================================================
 */
export const FetchPlainFromServer = async (
  apiName,
  payload,
  { method = "POST", timeout = 10000, open = false } = {}
) => {
  if (!apiName) throw new Error("API name is required");

  const url = ABSOLUTE_URL.test(apiName)
    ? apiName
    : `${open ? getOpenBaseUrl() : BASE_URL}${apiName}`;

  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);

  try {
    const res = await authFetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
      },
      body: payload ? JSON.stringify(payload) : undefined,
      signal: controller.signal,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`HTTP ${res.status}: ${text}`);
    }

    return await res.json();
  } finally {
    clearTimeout(id);
  }
};
